/*
 * @providesModule SKZTournament
 *
 *
 * Created by Skillz on 2/19/16.
 * Copyright © 2016 Skillz. All rights reserved.
 *
 */

import _ from 'lodash';
import ClientPropertyConstants from 'ClientPropertyConstants';
import type {CustomPrize} from 'SKZPrize';
import {ETournamentType, EPrizeType} from 'MatchCellImage';
import I18n, {_m} from 'INTL';
import LiveEvent from 'SKZLiveEvent';
import Match from 'SKZMatch';
import type {MatchRule} from 'SKZMatchRule';
import moment from 'moment';
import NetClient from 'NetClient';
import Preferences from 'Preferences';
import LiveBracketTimerUtils from 'LiveBracketTimerUtils';
import type {Prize} from 'SKZPrize';
import Sizes from 'Sizes';
import SkillzStyle from 'SkillzStyle';
import SKZConstants from 'SKZConstants';
import SKZBracket from 'SKZBracket';
import {getMedalIcon} from 'LeagueInfoUtils';

import {NativeModules, Platform} from 'react-native';

const SKZAPIManager = NativeModules.SKZAPIManager;
const SKZCoreDataWrapper = NativeModules.SKZCoreDataWrapper;
const SKZReactVCManager = NativeModules.SKZReactViewControllerManager;

const TournamentCategoryTypeValues = {
  SYNC: 'SYNC',
  EVENTS: 'EVENTS',
  BRACKET: 'BRACKET',
  H2H: 'H2H',
  CASH: 'CASH',
  FRIENDS: 'FRIENDS',
  LIVE: 'LIVE',
  Z: 'Z',
  RECOMMENDED: 'RECOMMENDED',
  LIVE_BRACKET: 'LIVE_BRACKET',
} as const;

export type TournamentCategoryType =
  (typeof TournamentCategoryTypeValues)[keyof typeof TournamentCategoryTypeValues];

export const TournamentCategoryType = TournamentCategoryTypeValues;

type TournamentType = 'UNLIMITED_ENTRY' | 'LIMITED_ENTRY' | 'TURN_BASED' | 'REMATCH';
type TournamentTurnBasedType = 'PER_ROUND_SCORE' | 'OBJECTIVE_ONLY' | 'FINAL_SCORE';
type TournamentBracketUserState = 'READY' | 'PENDING' | 'FINISHED';
type TournamentRegistrationRequired = 'BEFORE_JOIN' | 'AFTER_REPORT_SCORE' | 'NONE';
type TournamentScoringType = 'BEST' | 'LAST' | 'MEAN' | 'LAST_MEAN' | 'SUM';
type TournamentPrizeType = 'CUSTOM_PRIZE' | 'PRIZE';
export type PrizeTier = {prize: Prize; startPosition: number; endPosition: number};
type LimitedUseTournamentData = {
  type: 'TIME' | 'ENTRY_PLAYER' | 'ENTRY_TEMPLATE';
  entries_remaining: number | null;
  expiration: string | null;
  image_url: string | null;
  banner: {
    type: 'DEFAULT' | 'LIMITED_TIME' | 'SPECIAL_LIMITED_TIME' | 'CUSTOM';
    custom_text: string | null;
  };
};

export type Currency = {
  amount: string;
  type: 'CASH' | 'TICKETZ' | 'Z' | 'MEDAL';
  iconSource: string;
  textColor?: string;
};

export class TournamentRecord {
  id: string;
  name: string;
  description: string;
  set_id: number;

  cash_fee: number;
  discounted_cash_fee?: number;
  z_fee: number;

  currency_code: string;

  total_cash_prize: number;
  total_bonus_cash_prize: number;
  total_z_prize: number;
  total_ticketz_prize: number;
  ticketz_prize: number;
  total_medal_prize: number;
  medals_prizes: Array<number> | null;
  prizes: Array<Prize>;
  custom_prizes: Array<CustomPrize>;

  min_players: number;
  max_players: number;
  type: TournamentType;
  rules?: Array<MatchRule>;

  turn_based_type?: TournamentTurnBasedType;
  tutorial_image_url: string;

  unlimited_score_type: TournamentScoringType;
  unlimited_score_type_message: string;

  synchronous: boolean;

  viewed_ad: boolean;
  video_ad_entry_enabled: boolean;
  free_entry?: boolean;
  registration_required: TournamentRegistrationRequired;
  video_ad_entry_limit_reached: boolean;

  entries_required: number;
  featured: boolean;
  start: string;
  end: string;
  bracket?: {
    size: number;
    round_length_seconds: number;

    tie_breaker_length_seconds: number;
    cancellation_length_seconds: number;

    id?: string;
    num_entries?: number;
    user_state?: TournamentBracketUserState;
    user_expires_at: string;

    // New timing properties for live brackets
    show_start_time?: string;
    register_start_time?: string;
    register_end_time?: string;
    bracket_start_time?: string;
    bracket_end_time?: string;
    current_round?: number;
    current_round_start_time?: string;
    current_round_end_time?: string;
    to_notify_register_start?: boolean;
    live_bracket_player_state?: string;
  };
  details_image_url?: string;
  live_event?: LiveEvent;

  friend: boolean;
  incentive: boolean;
  match_code?: string;
  active_window_start?: string;
  active_window_end?: string;
  banner_text?: string;
  creator?: {
    user_id: number;
    display_name: string;
    avatar_url?: string;
  };
  off_system: boolean;
  current_challenge_count: number;
  max_challenge_count: number;
  seconds_to_next_challenge: number;
  challenge_refresh_seconds: number;
  recommended_friends_only: boolean;
  third_party_ads_enabled: boolean;

  prize_type: TournamentPrizeType;
  estimated_total_value: number;

  // Tournament template highlighting for home screen FTUE
  highlight?: boolean;

  /**
   * @deprecated SDK 29.0.0
   */
  custom_color: string;
  /**
   * @deprecated SDK 29.0.0
   */
  hosted_tournament_logo: string;
  hosted_tournament_promotion: {
    custom_color: string;
    logo_url: string;
  };

  play_tile_promotion: {
    badge_text: string;
    badge_logo_url: string;
  };

  vs_screen_promotion: {
    logo_url: string;
    background_url: string;
  };

  limited_use: LimitedUseTournamentData | null;
  sponsorship: boolean | null;

  category_type: TournamentCategoryType;
  highlight_text: string;
  // Determines the category of the tournament. For recommended tournaments this indicates the source of the recommendation
  category_source?: string;

  // object addition for discounted entry fee
  cash_fee_discount?: {
    discount_percent: number;
    discount_end_time: string;
    discounted_cash_fee: number;
  };

  prizes_as_percentage: boolean;
}

type CoreDataCustomPrize = {
  _isa: string;
  id: string;
  name: string;
  description: string;
  position: number;
  imageURL: string;
};

type CoreDataTournanemt = {
  _isa: string;
  entryFee: number;
  entryPoints: number;
  templateId: number;
  prize: number;
  prizePoints: number;

  entriesRequired: number;
  customPrizes?: Array<CoreDataCustomPrize>;
};

export type CurrencyViewProps = {
  amount: number;
  currencyCode: 'VIRTUAL' | 'TICKETZ' | 'USD' | 'INR';
};

export default class Tournament extends TournamentRecord {
  constructor(keyValueSource: Object) {
    super();
    Tournament._formatLiveEvent(keyValueSource);
    Tournament._setCurrencyCode(keyValueSource);
    Tournament._sumTotalMedals(keyValueSource);
    return _.create(Tournament.prototype, keyValueSource);
  }

  static make(tournaments: Array<TournamentRecord>): Array<TournamentRecord> {
    if (Array.isArray(tournaments)) {
      return _.map(
        tournaments,
        (tr: TournamentRecord): Tournament => _.create(Tournament.prototype, tr)
      );
    }
    return [];
  }

  static _formatLiveEvent(tournament?: Object) {
    if (tournament && tournament.live_event) {
      const liveEvent = tournament.live_event;
      tournament.live_event = new LiveEvent(liveEvent);
    }
  }

  //TODO: Defaults to USD for now.Set the currencycode correctly after INR changes are merged.
  static _setCurrencyCode(tournament?: Object) {
    if (tournament) {
      tournament.currency_code = 'USD';
    }
  }

  static _sumTotalMedals(tournament?: Object) {
    if (tournament && tournament.medals_prizes) {
      tournament.total_medal_prize = tournament.medals_prizes.reduce(
        (a: number, b: number): number => a + b,
        0
      );
    }
  }

  //checks if tournament 1 has a bigger payout than tournament 2
  static isGreaterThan(tournament1: Tournament, tournament2: Tournament): boolean {
    if (!tournament1 && !tournament2) {
      return false;
    }

    if (tournament1.total_cash_prize >= tournament2.total_cash_prize) {
      return true;
    } else if (tournament2.total_cash_prize == 0) {
      if (tournament1.total_ticketz_prize >= tournament2.total_ticketz_prize) {
        return true;
      } else if (tournament2.total_ticketz_prize == 0) {
        if (tournament1.total_z_prize >= tournament2.total_z_prize) {
          return true;
        }
      }
    }

    return false;
  }

  /** Overrides the name on the tournament  */
  set nameOverride(name: string) {
    this.name = name;
  }

  /** Overrides the description on the tournament  */
  set descriptionOverride(description: string) {
    this.description = description;
  }

  isCashEntry(): boolean {
    return Boolean(this.cash_fee);
  }

  isCashTournament(): boolean {
    return Boolean(this.total_cash_prize > 0 || this.total_bonus_cash_prize > 0);
  }

  isZTournament(): boolean {
    return Boolean(
      this.total_ticketz_prize == 0 && !this.isCashTournament() && !this.isCustomPrizeTournament()
    );
  }

  isPointsTournament(): boolean {
    return this.isZTournament();
  }

  isTicketzTournament(): boolean {
    return Boolean(
      this.total_ticketz_prize > 0.0 &&
        (this.custom_prizes === undefined || this.custom_prizes.length == 0)
    );
  }
  // Use this method going forward to check for permissions based on "real cash" gameplay
  isLocationRequired(): boolean {
    /**
     * for live brackets, the location is always required b/c there is no way to
     * determine the custom prize type, so it could either be a ticketz or cash prize
     *
     * TODO: update this check when we have a way to determine the custom prize type,
     * so ticketz tournaments can be played without location
     */
    if (this.isLiveBracket()) {
      return true;
    }

    return Boolean(
      this.isCashTournament() ||
        this.isZEntryBonusCashPrizeTournament() ||
        this.isCashEntry() ||
        this.isCustomPrizeTournament()
    );
  }
  hasMedals(): boolean {
    return Boolean(this.total_medal_prize);
  }

  isOffSystemFTTemplate(): boolean {
    return this.off_system;
  }

  isRecommendedOnlyFTTemplate(): boolean {
    return this.recommended_friends_only;
  }

  isBracket(): boolean {
    return this.bracket != null;
  }

  isFreeRoll(): boolean {
    return Boolean(this.cash_fee == 0 && this.z_fee == 0 && this.entries_required == 0);
  }

  isCustomPrizeTournament(): boolean {
    return (
      Boolean(Array.isArray(this.custom_prizes) && this.custom_prizes.length > 0) ||
      this.prize_type == 'CUSTOM_PRIZE'
    );
  }

  topCustomPrizeImage(): string {
    let imageForTopPrize = '';
    this.custom_prizes.some((prize: Prize): boolean => {
      if (prize.image_url) {
        imageForTopPrize = prize.image_url;
        return true;
      }
      return false;
    });

    return imageForTopPrize;
  }

  hasCustomPrizeImage(): boolean {
    if (this.custom_prizes) {
      const imagePrize = this.topCustomPrizeImage();
      if (imagePrize) {
        return true;
      }
    }
    return false;
  }

  getLiveEventEndsInString(finalString: string): string {
    if (this.live_event && !this.bracket) {
      let cutOffTime = 0;
      if (Platform.OS === 'android' || Platform.OS === 'web') {
        cutOffTime = new Date(
          parseInt(ClientPropertyConstants.getLiveEventJoinCutOff()) * SKZConstants.MS_PER_SEC
        );
      } else {
        cutOffTime = new Date(
          Preferences.get(SKZConstants.EVENT_JOIN_CUT_OFF_PERIOD) * SKZConstants.MS_PER_SEC
        );
      }

      const end = new Date(this.live_event.end) - cutOffTime;
      if (!this.isLiveEventBeingFinalized()) {
        return I18n.getSimplifiedTime(end);
      } else {
        return finalString;
      }
    }
    return '';
  }

  shouldShowEndSoonWarning(): boolean {
    if (this.live_event && !this.bracket) {
      const secondsUntilCutoff = Preferences.get(SKZConstants.EVENT_JOIN_CUT_OFF_PERIOD);
      const secondsBeforeWarn = Preferences.get(SKZConstants.EVENT_JOIN_WARN_PERIOD);
      const secondsUntilEnd =
        (new Date(this.live_event.end) - new Date()) / SKZConstants.MS_PER_SEC;
      return secondsUntilEnd < secondsUntilCutoff + secondsBeforeWarn;
    } else {
      return false;
    }
  }

  shouldRegisterBeforePlay(): boolean {
    return this.registration_required == 'BEFORE_JOIN';
  }

  shouldRegisterAfterPlay(): boolean {
    return this.registration_required == 'AFTER_REPORT_SCORE';
  }

  hasCashEntriesRemaining(): boolean {
    return this.cash_entries_remaining != 0;
  }

  hasVideoAdEntriesRemaining(): boolean {
    return this.video_ad_entries_remaining != 0 && this.video_ad_entry_enabled;
  }

  hasFreeEntriesRemaining(): boolean {
    return Boolean(this?.video_ad_entries_remaining != 0 && this?.free_entry);
  }

  isVideoAdEntryOnly(): boolean {
    return (
      this.cash_entries_remaining == 0 && this.live_event?.current_user_total_cash_entries == 0
    );
  }

  hasCashEntryLimit(): boolean {
    return this.cash_entries_remaining != null;
  }

  hasVideoAdEntryLimit(): boolean {
    return this.video_ad_entries_remaining != null;
  }

  hasEventEntryLimit(): boolean {
    return this.hasVideoAdEntryLimit() || this.hasCashEntryLimit();
  }

  hasReachedEntryLimit(): boolean {
    return (
      !this.hasCashEntriesRemaining() &&
      !this.hasVideoAdEntriesRemaining() &&
      !this.hasFreeEntriesRemaining()
    );
  }

  hasChangedToCashEntry(): boolean {
    return (
      (this.video_ad_entry_enabled || Boolean(this?.free_entry)) &&
      this.video_ad_entries_remaining == 0 &&
      this.live_event?.current_user_total_cash_entries == 0
    );
  }

  getEntriesRemaining(): string {
    let entryFee = null;
    let numEntries = 0;
    let moreThanOneType = false;
    if (this.hasFreeEntriesRemaining()) {
      entryFee = _m('Free');
      numEntries = this.hasVideoAdEntryLimit() ? this.video_ad_entries_remaining : -1;
      moreThanOneType = this.hasCashEntriesRemaining() || !this.hasCashEntryLimit();
    } else if (
      this.hasVideoAdEntriesRemaining() ||
      (!this.hasVideoAdEntryLimit() && this.video_ad_entry_enabled)
    ) {
      entryFee = _m('Watch Ad');
      numEntries = this.hasVideoAdEntryLimit() ? this.video_ad_entries_remaining : -1;
      moreThanOneType = this.hasCashEntriesRemaining() || !this.hasCashEntryLimit();
    } else if (!this.hasCashEntryLimit() || this.hasCashEntriesRemaining()) {
      numEntries = this.hasCashEntryLimit() ? this.cash_entries_remaining : -1;
      entryFee =
        this.cash_fee > 0 ? I18n.getCurrencyString(this.cash_fee, '$', true, true) : _m('Free');
    }

    if (numEntries < 0) {
      return _m('Unlimited {0} Entries', entryFee);
    } else if (numEntries == 0) {
      return _m('0 Entries Left');
    } else if (numEntries == 1) {
      return _m('{0} - {1} Entry Left{2}', numEntries, entryFee, moreThanOneType ? '*' : '');
    } else {
      return _m('{0} - {1} Entries Left{2}', numEntries, entryFee, moreThanOneType ? '*' : '');
    }
  }

  textForEventEntry(): string {
    if (this.hasVideoAdEntriesRemaining()) {
      return _m('Watch Ad');
    } else if (this.hasCashEntriesRemaining() && this.cash_fee > 0) {
      return I18n.getCurrencyString(this.cash_fee, '$', true, false);
    } else {
      return _m('Free');
    }
  }

  getEventEndsInTime(): number {
    if (this.isLiveBracket()) {
      return new Date(this.bracket?.user_expires_at);
    }
    const liveEvent = this.live_event;
    // convert S to MS
    let cutOffTime = 0;
    if (Platform.OS === 'android' || Platform.OS === 'web') {
      cutOffTime = new Date(
        parseInt(ClientPropertyConstants.getLiveEventJoinCutOff()) * SKZConstants.MS_PER_SEC
      );
    } else {
      cutOffTime = new Date(
        Preferences.get(SKZConstants.EVENT_JOIN_CUT_OFF_PERIOD) * SKZConstants.MS_PER_SEC
      );
    }
    const end = new Date(liveEvent.end) - cutOffTime;
    return end;
  }

  hasZEntryFee(): boolean {
    return this.z_fee > 0;
  }

  isBrandedTournament(): boolean {
    return this.hasBrandedColor() || this.hasTournamentLogo();
  }

  hasBrandedColor(): boolean {
    return (
      this.hosted_tournament_promotion?.custom_color?.length > 0 || this.custom_color?.length > 0
    );
  }

  getHostedTournamentCustomColor(): string {
    return this.hosted_tournament_promotion?.custom_color || this.custom_color;
  }

  hasTournamentLogo(): boolean {
    return (
      this.hosted_tournament_promotion?.logo_url.length > 0 ||
      this.hosted_tournament_logo?.length > 0
    );
  }

  getHostedTournamentLogo(): string {
    return this.hosted_tournament_promotion?.logo_url || this.hosted_tournament_logo;
  }

  adUnitForTournament(): string {
    if (Platform.OS == 'ios') {
      const moPubInfo = Preferences.get('mopubAdInfoSKZ');
      const freeRollInfo = moPubInfo ? moPubInfo.free_roll : {};
      if (freeRollInfo && Object.keys(freeRollInfo).length) {
        if (Sizes.isTablet()) {
          return Sizes.orientation({
            portrait: freeRollInfo[SKZConstants.SKZ_MOPUB_IPAD_PORTRAIT],
            landscape: freeRollInfo[SKZConstants.SKZ_MOPUB_IPAD_LANDSCAPE],
          });
        }
        return Sizes.orientation({
          portrait: freeRollInfo[SKZConstants.SKZ_MOPUB_IPHONE_PORTRAIT],
          landscape: freeRollInfo[SKZConstants.SKZ_MOPUB_IPHONE_LANDSCAPE],
        });
      } else {
        return '';
      }
    } else {
      const moPubInfoPrefs = Preferences.get('moPubAdInfo');
      const moPubInfo = moPubInfoPrefs ? JSON.parse(moPubInfoPrefs) : {};
      const freeRollInfo = moPubInfo ? moPubInfo.free_roll : {};

      if (freeRollInfo && Object.keys(freeRollInfo).length) {
        if (Sizes.isTablet()) {
          return Sizes.orientation({
            portrait: freeRollInfo[SKZConstants.SKZ_MOPUB_ANDROID_TABLET_PORTRAIT],
            landscape: freeRollInfo[SKZConstants.SKZ_MOPUB_ANDROID_TABLET_LANDSCAPE],
          });
        }
        return Sizes.orientation({
          portrait: freeRollInfo[SKZConstants.SKZ_MOPUB_ANDROID_PHONE_PORTRAIT],
          landscape: freeRollInfo[SKZConstants.SKZ_MOPUB_ANDROID_PHONE_LANDSCAPE],
        });
      } else {
        return '';
      }
    }
  }

  getTopPrize(): number {
    return (
      this.total_cash_prize + this.total_bonus_cash_prize ||
      this.total_z_prize ||
      this.total_ticketz_prize ||
      0
    );
  }

  getTopPrizeType(): string {
    if (this.isCashTournament()) {
      return 'CASH';
    } else if (this.total_z_prize) {
      return 'Z';
    } else if (this.total_ticketz_prize) {
      return 'TICKETZ';
    } else {
      return 'CUSTOM';
    }
  }

  /**
   * Can be used to create an object for @skillz/ui components requiring ICurrencyViewProps type
   * for the tournament's prize
   * @returns Object of type CurrencyViewProps for the given tournament
   */
  getPrizeDataForCurrencyView(): CurrencyViewProps {
    if (this.isCustomPrizeTournament()) {
      return {
        amount: this.estimated_total_value,
        currencyCode: I18n.currentCurrency(),
      };
    } else if (this.isPointsTournament()) {
      return {
        amount: this.total_z_prize,
        currencyCode: 'VIRTUAL',
      };
    } else if (this.isTicketzTournament()) {
      return {
        amount: this.total_ticketz_prize,
        currencyCode: 'TICKETZ',
      };
    } else {
      return {
        amount: this.total_cash_prize + this.total_bonus_cash_prize,
        currencyCode: I18n.currentCurrency(),
      };
    }
  }

  /**
   * Can be used to create an object for @skillz/ui components requiring ICurrencyViewProps type
   * for the tournament's entry fee
   * @returns Object of type CurrencyViewProps for the given tournament
   */
  getEntryDataForCurrencyView(): CurrencyViewProps {
    if (this.cash_fee > 0) {
      return {
        amount: this.cash_fee,
        currencyCode: I18n.currentCurrency(),
      };
    } else {
      return {
        amount: this.z_fee,
        currencyCode: 'VIRTUAL',
      };
    }
  }

  /**
   *
   * @param {Tournament} tournament: tournament that is compared against this
   * @returns true if this has a prize that is higher than tournament's prize, false otherwise
   */

  isHigherPrizeThan(tournament: Tournament): bool {
    if (this.isCashTournament() && tournament.isCashTournament()) {
      return (
        this.total_cash_prize + this.total_bonus_cash_prize >
        tournament.total_cash_prize + tournament.total_bonus_cash_prize
      );
    } else if (this.isCashTournament()) {
      return true;
    } else if (tournament.isCashTournament()) {
      return false;
    }

    if (this.isTicketzTournament() && tournament.isTicketzTournament()) {
      return this.total_ticketz_prize > tournament.total_ticketz_prize;
    } else if (this.isTicketzTournament()) {
      return true;
    } else if (tournament.isTicketzTournament()) {
      return false;
    }

    if (this.isZTournament() && tournament.isZTournament()) {
      return this.total_z_prize > tournament.total_z_prize;
    } else if (this.isZTournament()) {
      return true;
    } else if (tournament.isZTournament()) {
      return false;
    }
    return true; // it should never reach this stage but just for caution
  }

  prizeListForBracket(): Array<Prize | CustomPrize> {
    let prizeList = [];
    if (this.isCustomPrizeTournament()) {
      prizeList = this.custom_prizes;
    } else {
      prizeList = this.prizes;
    }

    const convertedList = [prizeList[0]];
    if (prizeList.length >= 2) {
      convertedList.push(prizeList[1]);
      for (let i = 2; Math.pow(2, i) <= prizeList.length; i++) {
        convertedList.push(prizeList[Math.pow(2, i) - 1]);
      }
    }
    return convertedList;
  }

  //TODO When we figure out relationship mapping, the following methods should move to event and/or bracket.
  isLiveBracket(): boolean {
    return Boolean(this.bracket && this.live_event);
  }

  /**
   * This method checks if tournament overlaps with another tournament:
   * - if the tournament `start date` is the same or before other tournament's `ending window`
   * And the `ending window` is the same or after the other tournament's `start date`, then the tournaments overlap
   * - `ending window` is calculated by adding the tournament duration to the tournament start time and padding time of 30 minutes
   * @param otherTournament the tournament to check for overlap with
   * @returns boolean indicating if the tournament overlaps with the other tournament
   */
  hasOverlappingLiveBracket(otherTournament: Tournament): boolean {
    if (
      !this.isLiveBracket() ||
      !otherTournament.isLiveBracket() ||
      !this.bracket ||
      !otherTournament.bracket
    ) {
      return false;
    }

    const thirtyMinutesInSeconds = 30 * SKZConstants.SECONDS_PER_MIN;

    const numRounds = SKZBracket.calculateNumRounds(this.bracket.size);
    const roundDuration = this.bracket.round_length_seconds;
    const tournamentDuration = numRounds * roundDuration;
    const otherTournamentNumRounds = SKZBracket.calculateNumRounds(otherTournament.bracket.size);
    const otherTournamentRoundDuration = otherTournament.bracket.round_length_seconds;
    const otherTournamentDuration = otherTournamentNumRounds * otherTournamentRoundDuration;

    const tournamentEndingWindow = moment(this.start).add(
      tournamentDuration + thirtyMinutesInSeconds,
      'seconds'
    );
    const otherTournamentEndingWindow = moment(otherTournament.start).add(
      otherTournamentDuration + thirtyMinutesInSeconds,
      'seconds'
    );

    return (
      moment(this.start).isSameOrBefore(otherTournamentEndingWindow) &&
      moment(tournamentEndingWindow).isSameOrAfter(otherTournament.start)
    );
  }

  isBracketJoinable(): boolean {
    return !this.isBracketFull() && !this.hasTournamentStarted() && !this.isPlayerRegistered();
  }

  isBracketFull(): boolean {
    if (this.bracket) {
      return this.bracket.size == this.bracket.num_entries;
    }
    return false;
  }

  hasTournamentStarted(): boolean {
    // For Live Brackets, use bracket_start_time if available, otherwise fall back to start
    if (this.isLiveBracket() && this.bracket?.bracket_start_time) {
      const startDate = moment(this.bracket.bracket_start_time);
      const now = moment();
      return Boolean(now >= startDate);
    }

    const startDate = moment(new Date(this.start));
    const now = moment();
    return Boolean(now >= startDate);
  }

  hasTournamentFinished(): boolean {
    const endDate = moment(new Date(this.end));
    const now = moment();
    return Boolean(now >= endDate);
  }

  hasEnteredLiveEvent(): boolean {
    if (!this.live_event) {
      return false;
    }

    return (
      this.live_event?.current_user_total_cash_entries ||
      this.live_event?.current_user_total_free_roll_entries
    );
  }

  shouldLiveEventFTUEJoin(): boolean {
    if (this.live_event && !this.bracket) {
      return this.hasTournamentStarted();
    } else if (this.live_event && this.bracket) {
      const startDate = new Date(this.start);
      const earliestJoin = moment(startDate).subtract(
        ClientPropertyConstants.getLiveEventFTUETimeframeSeconds(),
        'seconds'
      );
      return Date.now() >= earliestJoin;
    } else {
      return false;
    }
  }

  isPlayerRegistered(): boolean {
    if (this.live_event && this.bracket) {
      return !(this.bracket.user_state == null && this.bracket.user_expires_at == null);
    }
    return false;
  }

  isPlayerAbandoned(): boolean {
    const now = new Date();
    const expiresAt = new Date(this.bracket?.user_expires_at || -1);
    const hasUserExpired = now > expiresAt;
    return (
      Boolean(this.bracket && this.bracket.user_state == 'PENDING') &&
      hasUserExpired &&
      this.hasTournamentStarted()
    );
  }

  registeredPlayersOutOfTotalString(): string {
    const numEntries = _.get(this, 'bracket.num_entries', 0);
    const bracketSize = _.get(this, 'bracket.size', 0);
    return (numEntries ?? 0) + '/' + (bracketSize ?? 0);
  }

  isPlayerMatchReadyToPlay(): boolean {
    return Boolean(this.bracket && this.bracket.user_state == 'READY');
  }

  isPlayerWaitingForNextRound(): boolean {
    return Boolean(
      this.isLiveBracket() &&
        this.isPlayerRegistered() &&
        this.hasTournamentStarted() &&
        this.bracket?.user_state === 'PENDING'
    );
  }

  isPlayerCurrentlyInLiveBracket(): boolean {
    return Boolean(
      this.isLiveBracket() &&
        this.isBracketFull() &&
        this.isPlayerRegistered() &&
        this.hasTournamentStarted() &&
        (this.bracket?.user_state === 'READY' || this.bracket?.user_state === 'PENDING')
    );
  }

  isLiveBracketAboutToStart(): boolean {
    if (!this.isLiveBracket() || this.hasTournamentStarted()) {
      return false;
    }
    const secondsToShowLiveBracketToast =
      ClientPropertyConstants.getLiveBracketsShowToastBeforeStartSeconds();
    const remainingTimeToStart = moment(this.start).diff(moment(), 'seconds');
    return remainingTimeToStart <= secondsToShowLiveBracketToast;
  }

  isPlayerMatchFinished(): boolean {
    return this.bracket?.user_state === 'FINISHED';
  }

  /**
   * This method checks if the player has submitted a score for the last round of the live bracket.
   * BE will send this field as true only for last round. This will allow
   * FE to unblock user for further joins of all types of tournaments(not only live brackets)
   * @returns boolean indicating if the player has submitted a score for the last round
   */
  hideBottomSheetAndAllowFurtherJoins(): boolean {
    return this.bracket?.hide_bottom_sheet || false;
  }

  /**
   * Checks if registration is currently available for live brackets.
   * Registration is only available between register_start_time and register_end_time.
   * @returns boolean indicating if registration is currently available
   */
  isRegistrationAvailable(): boolean {
    if (!this.isLiveBracket() || !this.bracket) {
      return true; // Non-live brackets don't have this restriction
    }

    const now = moment();

    // If we have the new timing properties, use them
    if (this.bracket.register_start_time && this.bracket.register_end_time) {
      const registerStart = moment(this.bracket.register_start_time);
      const registerEnd = moment(this.bracket.register_end_time);
      return now.isBetween(registerStart, registerEnd, null, '[)'); // [) means inclusive start, exclusive end
    }

    // Fallback to existing logic if new properties aren't available
    return true;
  }

  /**
   * Checks if registration has closed for live brackets.
   * Registration is closed when the current time is after register_end_time.
   * @returns boolean indicating if registration has closed
   */
  isRegistrationClosed(): boolean {
    if (!this.isLiveBracket() || !this.bracket) {
      return false; // Non-live brackets don't have this restriction
    }

    const now = moment();

    // If we have the new timing properties, use them
    if (this.bracket.register_start_time && this.bracket.register_end_time) {
      const registerEnd = moment(this.bracket.register_end_time);
      return now.isAfter(registerEnd);
    }

    // Fallback to existing logic if new properties aren't available
    return false;
  }

  /**
   * Checks if the live bracket should be shown to users.
   * The bracket should be shown starting from show_start_time.
   * @returns boolean indicating if the bracket should be visible
   */
  shouldShowLiveBracket(): boolean {
    if (!this.isLiveBracket() || !this.bracket) {
      return true; // Non-live brackets are always shown
    }

    // If we have the new show_start_time property, use it
    if (this.bracket.show_start_time) {
      const showStart = moment(this.bracket.show_start_time);
      return moment().isSameOrAfter(showStart);
    }

    // Fallback to existing logic if new property isn't available
    return true;
  }

  textForPlayButton(): string {
    if (!this.isBracket()) {
      return '';
    }

    // live brackets - registration closed
    if (
      this.isLiveBracket() &&
      !this.hasTournamentStarted() &&
      this.isRegistrationClosed() &&
      !this.isPlayerRegistered()
    ) {
      return _m('REGISTRATION CLOSED');
    }

    // live brackets - visible but registration not yet available
    if (
      this.isLiveBracket() &&
      !this.hasTournamentStarted() &&
      !this.isRegistrationAvailable() &&
      !this.isPlayerRegistered()
    ) {
      return _m('NOTIFY ME');
    }

    // live brackets - event full
    if (
      this.isLiveBracket() &&
      !this.hasTournamentStarted() &&
      this.isBracketFull() &&
      !this.isPlayerRegistered()
    ) {
      return _m('EVENT FULL');
    }

    // live & async brackets - upcoming - user not registered
    if (!this.hasTournamentStarted() && !this.isPlayerRegistered()) {
      return _m('JOIN NOW');
    }

    // live & async brackets - upcoming - user registered
    if (!this.hasTournamentStarted() && this.isPlayerRegistered()) {
      return _m('REGISTERED');
    }

    // live brackets - tournament started and user is registered
    if (this.hasTournamentStarted() && this.isPlayerRegistered() && this.isLiveBracket()) {
      // Check if user is waiting for next round
      if (this.isPlayerWaitingForNextRound()) {
        const buttonText = LiveBracketTimerUtils.getLiveBracketButtonText(this);
        if (buttonText) {
          return buttonText;
        }
      }
    }

    // live & async brackets - inflight - user registered. For live brackets we use a different call out
    if (
      this.hasTournamentStarted() &&
      this.isBracketFull() &&
      this.isPlayerRegistered() &&
      this.isPlayerMatchReadyToPlay()
    ) {
      return this.isLiveBracket() ? _m('READY TO PLAY') : _m('PLAY NOW');
    }

    // live & async brackets - inflight - user registered. For live brackets we use a different call out
    if (this.isPlayerWaitingForNextRound()) {
      return _m('NEXT ROUND STARTS SOON');
    }

    // live & async brackets - inflight - user did not register but they can watch the event
    // note: this state won't happen for live brackets because on this scenario we're not showing the tile to the user
    if (this.hasTournamentStarted() && this.isBracketFull() && !this.isPlayerRegistered()) {
      return _m('WATCH NOW');
    }

    return '';
  }

  textForCashEntryButton(): string {
    if (this.cash_fee == 0) {
      return _m('PLAY FREE');
    }
    return _m('PAY ') + this.formatted_entry_fee;
  }

  updateEventSubscription(shouldSubscribe: boolean) {
    const url = 'tournaments/' + this.id + '/notification_subscriptions';
    const retryMode = SKZAPIManager.OnError.failSilently;
    const httpMethod = shouldSubscribe ? 'POST' : 'DELETE';
    NetClient.fetchURL(httpMethod, url, null, null, retryMode)
      ?.then((payload: Object) => {
        console.debug('successful subscribe: ' + httpMethod + ' ' + JSON.stringify(payload));
      })
      .catch((error: Object) => {
        console.warn('error on event APNS subscribe: ' + httpMethod + ' ' + JSON.stringify(error));
      });
  }

  insertTournamentIntoCoreData(completion: () => void) {
    this.insertTournamentOfTypeIntoCoreData('SKZTemplateTournament', null, completion);
  }

  insertRematchTournamentIntoCoreData(match: Match, completion: () => void) {
    this.insertTournamentOfTypeIntoCoreData('SKZTournamentSummary', match, completion);
  }

  insertChallengeTournamentIntoCoreData(match: Match, completion: () => void) {
    this.insertTournamentOfTypeIntoCoreData('SKZChallengeTournament', match, completion);
  }

  insertTournamentOfTypeIntoCoreData(type: string, match: Match, completion: () => void) {
    if (Platform.OS == 'android') {
      if (completion) {
        completion();
      }
      return;
    }
    const tournament = {
      _isa: type,
      entryFee: this.cash_fee,
      entryPoints: this.z_fee,
      templateId: parseInt(this.id),
      prize: this.total_cash_prize,
      prizePoints: this.total_z_prize,
      entriesRequired: this.entries_required,
      description: this.description,
      name: this.name,
      expire: this.end,
      rules: this.rules,
      synchronous: this.synchronous,
      type: this.matchTypeEnumFromString(this.type),
    };

    if (match) {
      tournament.id = parseInt(match.id);
    }
    const entryOffers = [];
    if (this.live_event) {
      tournament.event = {
        _isa: 'SKZEvent',
        name: this.live_event.name,
        logo: this.live_event.logo,
        startDate: this.live_event.start,
        endDate: this.live_event.end,
        description: this.live_event.description,
        id: this.live_event.id,
        eventUrl: this.live_event.event_url,
        streamUrl: this.live_event.stream_url,
        signupUrl: this.live_event.signup_url,
        entries: this.live_event.entries,
        userRegistered: this.live_event.user_registered,
        shareMessage: this.live_event.share_message,
      };

      if (this.live_event.ticket_offers && this.live_event.ticket_offers.length) {
        this.live_event.ticket_offers.forEach((ticketOffer: Object) => {
          entryOffers.push({
            _isa: 'SKZEventEntryOffer',
            id: ticketOffer.id,
            title: ticketOffer.title,
            amount: ticketOffer.amount,
            entries: ticketOffer.entries,
            start: ticketOffer.start,
            end: ticketOffer.end,
            event: tournament.event,
          });
        });
      }
    }

    //add prizes
    if (this.isCustomPrizeTournament()) {
      tournament.customPrizes = [];
      if (this.custom_prizes.length > 0) {
        const firstPrize = this.custom_prizes[0];
        tournament.customPrizes.push({
          _isa: 'SKZCustomPrize',
          id: firstPrize.position + '-' + firstPrize.name + '-' + this.id,
          name: firstPrize.name,
          description: firstPrize.description,
          position: firstPrize.position,
          imageURL: firstPrize.image_url,
        });
      }
    }

    SKZCoreDataWrapper.createCoreDataObject(tournament, () => {
      if (entryOffers.length > 0) {
        SKZCoreDataWrapper.createCoreDataObject(entryOffers, () => {
          if (completion) {
            completion();
          }
        });
      } else {
        if (completion) {
          completion();
        }
      }
    });
  }

  matchTypeEnumFromString(typeString: string): number {
    switch (typeString) {
      case Tournament.Type.REMATCH:
        return 4;
      case Tournament.Type.TURN_BASED:
        return 3;
      case Tournament.Type.UNLIMITED_ENTRY:
        return 1;
      case Tournament.Type.LIMITED_ENTRY:
        return 0;
      default:
        return -1;
    }
  }

  prizeTiersForLiveEvent(): Array<PrizeTier> {
    const prizeTiers = [];
    let prizes = [];
    if (this.isCustomPrizeTournament()) {
      prizes = this.custom_prizes;
    } else {
      prizes = this.prizes;
    }

    let currentPrizeRange = null;
    let lastPrizeAmount = -1;

    _.forEach(prizes, (prize: Prize) => {
      if (
        !this.isCustomPrizeTournament() &&
        (prize.cash_prize || prize.z_prize) == lastPrizeAmount
      ) {
        currentPrizeRange.endPosition = prize.position;
      } else {
        lastPrizeAmount = prize.cash_prize || prize.z_prize;
        currentPrizeRange = {
          prize: prize,
          startPosition: prize.position,
          endPosition: prize.position,
        };
        prizeTiers.push(currentPrizeRange);
      }
    });

    return prizeTiers;
  }

  getTextForBracketRegistrationPlayerCount(): string {
    const playersEntered = this.bracket?.num_entries;
    const maxPlayers = this.bracket?.size;
    const isTournamentTemplateUIV2Enabled =
      ClientPropertyConstants.getTournamentTemplateUIV2Enabled();
    //For new UI match template we will show the number of players entered always.
    if (playersEntered == maxPlayers && !isTournamentTemplateUIV2Enabled) {
      return _m('EVENT FULL');
    } else {
      return this.registeredPlayersOutOfTotalString();
    }
  }

  static determineTournamentCategory(tournament: TournamentRecord): string {
    if (tournament.synchronous) {
      return 'SYNC';
    }
    if (tournament.live_event) {
      return 'LIVE';
    }

    if (tournament.bracket) {
      return 'BRACKET';
    }

    if (tournament.friend) {
      return 'FRIENDS';
    }

    if (tournament.isCashTournament()) {
      return 'CASH';
    }

    return 'Z';
  }

  static getETournamentTypeFromString(tournamentCategory: string): ETournamentType {
    switch (tournamentCategory) {
      case 'SYNC':
        return ETournamentType.SYNC;
      case 'LIVE':
        return ETournamentType.LIVE;
      case 'BRACKET':
        return ETournamentType.BRACKET;
      case 'FRIENDS':
        return ETournamentType.FRIENDS;
      case 'CASH':
        return ETournamentType.CASH;
      default:
        return ETournamentType.PRACTICE;
    }
  }

  static getEPrizeTypeFromTournament(tournament: Tournament): EPrizeType {
    if (tournament.isCashTournament()) {
      return tournament.total_bonus_cash_prize > 0 ? EPrizeType.BONUS_CASH : EPrizeType.CASH;
    } else if (tournament.isPointsTournament()) {
      return EPrizeType.VIRTUAL_COIN;
    } else if (tournament.isTicketzTournament()) {
      return EPrizeType.TICKETZ;
    } else {
      return EPrizeType.CUSTOM;
    }
  }

  getPrizeString(): string {
    const ONE_THOUSAND = 1000;

    if (this.isCashTournament()) {
      const cashPrizeToConvert = this.total_cash_prize + this.total_bonus_cash_prize;
      if (cashPrizeToConvert >= ONE_THOUSAND) {
        return `${I18n.currencySymbol()}${I18n.abbreviatedNumber(cashPrizeToConvert)}`;
      }
      return I18n.getCurrencyString(cashPrizeToConvert, '$', true, true);
    } else if (this.total_z_prize > 0.0) {
      if (this.total_z_prize > ONE_THOUSAND) {
        const zPrizeNum =
          Platform.OS == 'ios'
            ? this.total_z_prize
            : Math.round(this.total_z_prize / ONE_THOUSAND) * ONE_THOUSAND;
        return I18n.abbreviatedNumber(zPrizeNum);
      }
      return this.total_z_prize.toString();
    } else if (this.isTicketzTournament()) {
      return I18n.abbreviatedNumber(this.getTopPrize());
    }

    return '★';
  }

  static iconForTournamentCategory(tournamentCategory: TournamentCategoryType): string {
    const FRIENDS = 'FRIENDS',
      SYNC = 'SYNC',
      LIVE = 'LIVE',
      CASH = 'CASH',
      Z = 'Z',
      BRACKET = 'BRACKET',
      RECOMMENDED = 'RECOMMENDED',
      RECOMMENDEDV2 = 'RECOMMENDEDV2' as TournamentCategoryType,
      LIVE_BRACKET = 'LIVE_BRACKET' as TournamentCategoryType;

    const isTournamentTemplateV2 = ClientPropertyConstants.getTournamentTemplateUIV2Enabled();
    //TODO: for Sync icon type, return correct icon when received.
    //TODO: insert correct icon for VS Friends tournaments and use correct constant value
    switch (tournamentCategory) {
      case FRIENDS:
        return 'icon_play_friends.svg';
      case SYNC:
        return !isTournamentTemplateV2 ? 'icon_play_live.svg' : 'icon_play_live_v2.svg';
      case LIVE:
        return !isTournamentTemplateV2 ? 'icon_play_events.svg' : 'icon_play_events_v2.svg';
      case CASH:
        return !isTournamentTemplateV2 ? 'icon_play_headtohead.svg' : 'icon_play_headtohead_v2.svg';
      case Z:
        return !isTournamentTemplateV2 ? 'icon_play_practice.svg' : 'icon_play_practive_v2.svg';
      case BRACKET:
        return !isTournamentTemplateV2 ? 'icon_play_brackets.svg' : 'icon_play_brackets_v2.svg';
      case RECOMMENDED:
      case RECOMMENDEDV2:
        return 'icon_play_recommended.svg';
      case LIVE_BRACKET:
        return 'LOTTIE_LIVE_BRACKET'; // Special identifier for Lottie animation
    }
    return '';
  }

  isThreePlusH2H(): boolean {
    const tournamentCategory = Tournament.determineTournamentCategory(this);
    const isH2H = tournamentCategory === 'CASH' || tournamentCategory === 'Z';
    return isH2H && this.max_players > 2;
  }

  isTwoPlayerH2H(): boolean {
    const tournamentCategory = Tournament.determineTournamentCategory(this);
    const isH2H = tournamentCategory === 'CASH' || tournamentCategory === 'Z';
    return isH2H && this.max_players === 2;
  }

  getPrizeAtIndex(index: number): Prize {
    if (index > this.prizes.length - 1) {
      return null;
    }
    return this.prizes[index];
  }

  getPrizeForPosition(position: number): Object {
    let prizeAtPosition =
      _.find(this.prizes, function (prize: Object): boolean {
        return prize.position == position;
      }) || null;
    if (!prizeAtPosition) {
      prizeAtPosition =
        _.find(this.custom_prizes, function (prize: Object): boolean {
          return prize.position == position;
        }) || null;
    }
    return prizeAtPosition;
  }

  getCustomPrizeAtIndex(index: number): CustomPrize {
    if (index > this.custom_prizes.length - 1) {
      return null;
    }
    return this.custom_prizes[index];
  }

  canUserJoin(user) {
    if (this.cash_fee > user.cash_balance && !this.isPointsTournament()) {
      SKZReactVCManager.presentReactControllerForModule(
        'InsufficientFunds',
        false,
        {
          modalPresentationStyle: 5,
          isCash: true,
        },
        () => {
          console.debug('Pushing insufficient Funds');
        }
      );
    } else if (this.z_fee > user.z_balance && this.isPointsTournament()) {
      // Same for now
      SKZReactVCManager.presentReactControllerForModule(
        'InsufficientFunds',
        false,
        {
          modalPresentationStyle: 5,
          isCash: false,
        },
        () => {
          console.debug('Pushing insufficient Funds');
        }
      );
    }
  }

  isLiveEventBeingFinalized(): boolean {
    if (this.live_event) {
      let cutOffTime = 0;

      if (Platform.OS === 'android' || Platform.OS === 'web') {
        cutOffTime = new Date(
          parseInt(ClientPropertyConstants.getLiveEventJoinCutOff()) * SKZConstants.MS_PER_SEC
        );
      } else {
        cutOffTime = new Date(
          Preferences.get(SKZConstants.EVENT_JOIN_CUT_OFF_PERIOD) * SKZConstants.MS_PER_SEC
        );
      }

      const end = new Date(this.live_event.end) - cutOffTime;
      if (Date.now() > end) {
        return true;
      }
    }

    return false;
  }

  isLiveEventConcluded(): boolean {
    if (this.live_event) {
      const end = new Date(this.live_event.end);
      if (Date.now() > end) {
        return true;
      }
    }

    return false;
  }

  isFriendTournament(): boolean {
    return this.isIncomingInvite() || this.friend;
  }

  isIncomingInvite(): boolean {
    return this.creator != null;
  }

  isIncomingInviteFromOtherUser(currentUserId: string): boolean {
    return this.isIncomingInvite() && this.creator.user_id != currentUserId;
  }

  isIncentiveTournament(): boolean {
    return this.incentive;
  }

  getCashEntryFee(): number {
    if (this.discounted_cash_fee !== undefined) {
      return this.discounted_cash_fee;
    } else if ((this.cash_fee_discount?.discounted_cash_fee ?? -1) >= 0) {
      return this.cash_fee_discount?.discounted_cash_fee ?? this.cash_fee;
    }
    return this.cash_fee;
  }

  get formatted_entry_fee(): string {
    let formatted_fee = '';
    if (this.entries_required) {
      formatted_fee = I18n.formattedMessage('{0} Entry', 1);
    } else if (
      this.cash_fee_discount?.discounted_cash_fee !== undefined &&
      this.cash_fee_discount.discounted_cash_fee >= 0
    ) {
      const discountedCashFee = I18n.getCurrencyString(
        this.cash_fee_discount.discounted_cash_fee,
        '$',
        true,
        true
      );
      formatted_fee = discountedCashFee;
    } else if (this.cash_fee > 0.0) {
      const cashFee = I18n.getCurrencyString(this.cash_fee, '$', true, true);
      formatted_fee = cashFee;
    } else if (this.z_fee > 0.0) {
      const zFee =
        SkillzStyle.currencyValues.practiceCurrencyString + I18n.getIntegerString(this.z_fee);
      formatted_fee = zFee;
    } else {
      formatted_fee = I18n.message('Free Entry');
    }
    return formatted_fee;
  }

  get formatted_entry_fee_V2(): string {
    let formatted_fee = '';
    if (this.entries_required) {
      formatted_fee = I18n.formattedMessage('{0} Entry', 1);
    } else if (this.cash_fee > 0.0) {
      const cashFee = I18n.getCurrencyString(this.cash_fee, '', true, true);
      formatted_fee = cashFee;
    } else if (this.z_fee > 0.0) {
      const zFee = I18n.getIntegerString(this.z_fee);
      formatted_fee = zFee;
    } else {
      formatted_fee = I18n.message('Free Entry');
    }
    return formatted_fee;
  }

  get formatted_prize(): string {
    let formatted_prize = '';
    if (this.isCustomPrizeTournament()) {
      const customPrize = this.getCustomPrizeAtIndex(0);
      if (customPrize) {
        formatted_prize = customPrize.name;
      }
    } else if (this.isPointsTournament()) {
      const zPrize =
        SkillzStyle.currencyValues.practiceCurrencyString +
        I18n.getIntegerString(this.total_z_prize);
      formatted_prize = zPrize;
    } else if (this.isTicketzTournament()) {
      formatted_prize =
        I18n.abbreviatedNumber(this.total_ticketz_prize) + ' ' + I18n.message('Ticketz');
    } else if (this.total_bonus_cash_prize > 0.0) {
      const cashPrize = I18n.getCurrencyString(this.total_bonus_cash_prize, '$', true, true);
      formatted_prize = cashPrize + ' ' + I18n.message('Bonus Cash');
    } else {
      const cashPrize = I18n.getCurrencyString(this.total_cash_prize, '$', true, true);
      formatted_prize = cashPrize;
    }
    return formatted_prize;
  }

  getCurrentChallengeCount(): number {
    return this.current_challenge_count;
  }

  getMaxChallengeCountNumber(): number {
    return this.max_challenge_count;
  }

  getSecondsToNextChallengeNumber(): number {
    return this.seconds_to_next_challenge;
  }

  getChallengeRefreshSecondsNumber(): number {
    return this.challenge_refresh_seconds;
  }

  hasUnlimitedChallenges(): boolean {
    return (
      this.isFriendTournament() &&
      !this.getCurrentChallengeCount() &&
      !this.getMaxChallengeCountNumber() &&
      !this.getSecondsToNextChallengeNumber() &&
      !this.getChallengeRefreshSecondsNumber()
    );
  }

  shouldShowFreeEntryBanner(): boolean {
    return this.isOffSystemFTTemplate() && this.z_fee === 0 && this.cash_fee === 0;
  }

  hasMaxChallenges(): boolean {
    return this.current_challenge_count === this.max_challenge_count;
  }

  isThirdPartyAdsEnabled(): boolean {
    return this.third_party_ads_enabled;
  }

  isSponsoredTournament(): boolean {
    return this.sponsorship ?? false;
  }

  /** Per product, if it's a 2 player H2H match, only show winner's medals. Otherwise show sum of medals */
  getCombinedMedalsForMatchSelectCell(): number {
    if (!this.medals_prizes) {
      return 0;
    }

    if (this.isTwoPlayerH2H()) {
      return this.medals_prizes.length > 0 ? this.medals_prizes[0] : 0;
    }
    return this.total_medal_prize;
  }

  /** Limited Use Tournaments: methods start */
  isLimitedUseTournament(): boolean {
    return Boolean(this.limited_use);
  }

  isDiscountedTournamentApplicable(): boolean {
    return Boolean(this.cash_fee_discount);
  }

  getLimitedUseTournamentType(): string | undefined {
    return this.limited_use?.type;
  }

  getLimitedUseTournamentEntries(): number | null | undefined {
    return this.limited_use?.entries_remaining;
  }

  hasLimitedUseTournamentImage(): boolean {
    return Boolean(this.limited_use?.image_url);
  }

  getLimitedUseTournamentBannerType(): string | undefined {
    return this.limited_use?.banner.type;
  }

  hasLimitedUseTournamentCustomBannerText(): boolean {
    return Boolean(this.limited_use?.banner.custom_text);
  }

  isZEntryBonusCashPrizeTournament(): boolean {
    return Boolean(
      this.total_bonus_cash_prize > 0 && this.hasZEntryFee() && this.isLimitedUseTournament()
    );
  }

  isAnyZEntryBonusCashPrizeTournament(): boolean {
    return Boolean(this.total_bonus_cash_prize > 0 && this.hasZEntryFee());
  }

  getLimitedUseTournamentEndsInString(finalString: string): string {
    if (!this.isLimitedUseTournament() || this.getLimitedUseTournamentType() !== 'TIME') {
      return '';
    }
    const cutOffTime = new Date(
      parseInt(ClientPropertyConstants.getLimitedUseTournamentJoinCutOff()) *
        SKZConstants.MS_PER_SEC
    );
    const end = new Date(this.limited_use.expiration) - cutOffTime;
    if (!this.isLimitedUseTournamentBeingFinalized()) {
      return I18n.getSimplifiedTime(end);
    } else {
      return finalString;
    }
  }

  isLimitedUseTournamentBeingFinalized(): boolean {
    if (!this.isLimitedUseTournament() || this.getLimitedUseTournamentType() !== 'TIME') {
      return false;
    }
    const cutOffTime = new Date(
      parseInt(ClientPropertyConstants.getLimitedUseTournamentJoinCutOff()) *
        SKZConstants.MS_PER_SEC
    );
    const end = new Date(this.limited_use.expiration) - cutOffTime;
    if (Date.now() > end) {
      return true;
    }
    return false;
  }

  hasLimitedUseTournamentReachedEntryLimit(): boolean {
    const limitedEntriesTypes: Array<LimitedUseTournamentData['type']> = [
      'ENTRY_PLAYER',
      'ENTRY_TEMPLATE',
    ];
    const limitedUseTournamentType =
      this.getLimitedUseTournamentType() as LimitedUseTournamentData['type'];

    if (!this.isLimitedUseTournament() || !limitedEntriesTypes.includes(limitedUseTournamentType)) {
      return false;
    }

    return this.getLimitedUseTournamentEntries() === 0;
  }

  /** Limited Use Tournaments: methods end */

  static Type = {
    LIMITED_ENTRY: 'LIMITED_ENTRY',
    UNLIMITED_ENTRY: 'UNLIMITED_ENTRY',
    REMATCH: 'REMATCH',
    TURN_BASED: 'TURN_BASED',
  };

  /**
   * Maps tournament prize data to an array of currency objects for display.
   *
   * This function extracts and processes different types of tournament prizes.
   * It creates an array of currency objects, each containing the amount, type, icon source, and text
   * color (optional). These objects are used to display the total tournament's prize information to the user.
   * @returns {Array<Object>} An array of currency objects, each representing a prize type.
   */

  mapPrizesToCurrency(): Currency[] {
    const currencies: Currency[] = [];
    if (this.total_cash_prize > 0 || this.total_bonus_cash_prize > 0 || this.custom_prizes) {
      let amount = this.total_cash_prize + this.total_bonus_cash_prize;
      // for live brackets, we don't want to show the estimated total value
      if (this.custom_prizes && !this.isLiveBracket()) {
        amount += this.estimated_total_value;
      }
      if (amount > 0) {
        currencies.push({
          amount: amount.toLocaleString(),
          type: 'CASH',
          iconSource: 'ic_cash_usd_background.png',
          textColor: SkillzStyle.fullStyles.textColorCash.color,
        });
      }
    }
    if (this.total_ticketz_prize > 0 || this.ticketz_prize > 0) {
      currencies.push({
        amount: (this.total_ticketz_prize + this.ticketz_prize).toLocaleString(),
        type: 'TICKETZ',
        iconSource: 'ic_ticketz_background.png',
        textColor: SkillzStyle.fullStyles.textColorTicketz.color,
      });
    }
    if (this.total_z_prize > 0) {
      currencies.push({
        amount: this.total_z_prize.toLocaleString(),
        type: 'Z',
        iconSource: SkillzStyle.fullStyles.practiceCurrencyFullIconLargeURI,
        textColor: SkillzStyle.fullStyles.textColorZ.color,
      });
    }
    if (this.medals_prizes && this.medals_prizes.length > 0) {
      currencies.push({
        amount: this.medals_prizes.reduce((acc, prize) => acc + prize, 0).toLocaleString(),
        type: 'MEDAL',
        iconSource: getMedalIcon(this.isCashTournament()),
        textColor: SkillzStyle.fullStyles.textColorPrimary.color,
      });
    }

    return currencies;
  }
}
